import { z } from "zod";
import { CONVERSATION_ID_PATTERN } from "../iam/ws-ticket";
/**
 * Conversation titles for the trainer's conversation list (plan D32).
 *
 * Repo `D78` keeps message content out of D1: the conversation index holds ids
 * and timestamps, and the transcript stays in the trainer's Durable Object. So
 * the title lives there too, and `apps/api`'s `GET /me/conversations` asks for
 * it over the `env.AI` service binding with `conversationTitles(input)`, the
 * RPC method `apps/ai` exposes on its `WorkerEntrypoint`. The title never
 * leaves the object any other way, and `apps/api` never stores it.
 */
/** How many conversations one call may ask about: the list's most recent 20. */
export const CONVERSATION_TITLES_MAX_IDS = 20;
/**
 * The longest title, in UTF-16 code units, with the `…` included. It matches
 * `title: z.string().min(1).max(80)` on the HTTP list, which Zod measures the
 * same way.
 */
export const CONVERSATION_TITLE_MAX_LENGTH = 80;
/**
 * The RPC input. `strictObject`, so a field renamed on the `apps/api` side of
 * the binding throws on the first call and names the field (`T-35`).
 *
 * `accountId` is the account `apps/api` took from the verified bearer. The
 * object names are BUILT from it, so a call can only reach that account's own
 * conversations.
 */
export const conversationTitlesRpcInput = z.strictObject({
    accountId: z.string().uuid(),
    conversationIds: z
        .array(z.string().regex(CONVERSATION_ID_PATTERN))
        .max(CONVERSATION_TITLES_MAX_IDS),
});
/** One title: the first user message, truncated. */
export const conversationTitle = z
    .string()
    .min(1)
    .max(CONVERSATION_TITLE_MAX_LENGTH);
/**
 * Every requested id is a key. `null` means the conversation has no user turn
 * yet, or its Durable Object failed or did not answer in time.
 */
export const conversationTitlesOutput = z.object({
    titles: z.record(z.string(), conversationTitle.nullable()),
});
/**
 * The RPC input for a title the PERSON set (`PUT /me/conversations/:id/title`).
 * `strictObject` for the reason {@link conversationTitlesRpcInput} gives; the
 * object's name is BUILT from `accountId` and `conversationId`, so a hole in
 * either would name an object that never existed and title it.
 *
 * `apps/api` has already checked that the row is the caller's own, live and
 * not frozen; the far side only stores.
 */
export const conversationRenameRpcInput = z.strictObject({
    accountId: z.string().uuid(),
    conversationId: z.string().regex(CONVERSATION_ID_PATTERN),
    title: z.string().trim().min(1).max(CONVERSATION_TITLE_MAX_LENGTH),
});
/** The title as the object stored it. Parsed by `apps/api` (`T-35`). */
export const conversationRenameAck = z.object({ title: conversationTitle });
//# sourceMappingURL=conversation-titles.js.map